import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {IElement} from '../../../incase-domain/elements';
import {ELEMENT} from '../../../incase-domain/elements/base/ElementTypes';
import {IElementImageMarkup} from '../../../incase-domain/elements/question/imageMarkup';
import {selectedPresentationSelector} from '../../../store/selectors/presentation.selectors';
import CanvasHeatmap, {HeatmapPoint} from '../CanvasHeatmap';
import CanvasQuestionMarkedAreas from '../CanvasQuestionMarkedAreas';

interface ImageMarkupHeatmapProps {
    elementId: string;
    points?: HeatmapPoint[];
}

function isImageMarkup(el?: IElement): el is IElementImageMarkup {
    return !!el && el.type === ELEMENT.IMAGE_MARKUP;
}

function ImageMarkupHeatmap(props: ImageMarkupHeatmapProps): JSX.Element | null {
    const {elementId, points} = props;

    const imgRef = useRef<HTMLImageElement | null>(null);

    const [dimensions, setDimensions] = useState<{width: number; height: number}>({width: 100, height: 100});

    const presentation = useSelector(selectedPresentationSelector);

    const element = useMemo(() => {
        return presentation.slides.flatMap((slide) => slide.elements).find((el) => el.id === elementId);
    }, [presentation.slides, elementId]);
    const imgSrc = isImageMarkup(element) ? element.imageSrc : undefined;

    const updateDimensions = useCallback((): void => {
        const imgEl = imgRef.current;
        if (!imgEl) return;

        const rect = imgEl.getBoundingClientRect();
        const width = Math.round(rect.width);
        const height = Math.round(rect.height);

        if (width > 0 && height > 0) {
            setDimensions({width, height});
        }
    }, []);

    useEffect(() => {
        const img = imgRef.current;
        if (!img) return undefined;

        const handleLoad = (): void => {
            requestAnimationFrame(() => {
                updateDimensions();
            });
        };

        if (img.complete) {
            handleLoad();
        } else {
            img.addEventListener('load', handleLoad);
        }

        return () => {
            img.removeEventListener('load', handleLoad);
        };
    }, [updateDimensions]);

    useEffect(() => {
        const handleResize = (): void => {
            requestAnimationFrame(() => {
                updateDimensions();
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [updateDimensions]);

    useEffect(() => {
        const img = imgRef.current;
        if (!img) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && entry.intersectionRatio > 0.75) {
                        requestAnimationFrame(() => {
                            updateDimensions();
                        });
                    }
                });
            },
            {threshold: 0.1}
        );

        observer.observe(img);

        return () => {
            observer.disconnect();
        };
    }, [updateDimensions]);

    if (!imgSrc) return null;

    return (
        <div className="c-imageMarkupHeatmap">
            <img ref={imgRef} src={imgSrc} alt="markup-heatmap" className="c-imageMarkupHeatmap__image" />
            <div className="c-imageMarkupHeatmap__overlay" />
            {isImageMarkup(element) && element.markedAreas && element.markedAreas.length > 0 && (
                <CanvasQuestionMarkedAreas markedAreas={element.markedAreas} width={dimensions.width} height={dimensions.height} />
            )}
            {points && points.length > 0 && (
                <CanvasHeatmap points={points} width={dimensions.width} height={dimensions.height} radius={dimensions.width / 50} />
            )}
        </div>
    );
}

export default ImageMarkupHeatmap;
